import API from './API';

// -----------------------------------------------------------------------------
//  RICHIESTE DI PRENOTAZIONE
//
//  ⚠️ Una richiesta NON è un booking: vive in una tabella a parte
//  (booking_request) e diventa una prenotazione vera solo dopo la conversione
//  (POST :id/convert), che valorizza `bookingId`. Finché non è convertita non
//  compare in /booking, quindi non la si vede nelle altre schede della board.
//
//  ⚠️ Questo controller NON è @nestjsx/crud: niente join/filter/sort via query
//  string. L'unico parametro è ?status=. Le relazioni che servono alla lista
//  (customer, pickup_point, dropoff_point) arrivano già unite dal server;
//  partner e sede NO — ci sono solo gli id (partnerId / headquarterId).
//
//  Visibilità lato server: i ruoli globali (il gestionale Simba) vedono tutto,
//  incluse le richieste ancora senza partner — che sono proprio quelle da
//  assegnare a mano.
// -----------------------------------------------------------------------------

// Stati della richiesta -> vocabolario UI. `pending` è la coda di lavoro.
export const REQUEST_STATES = {
  pending: { kind: 'attn', label: 'Da valutare' },
  counter_proposed: { kind: 'active', label: 'Contro-proposta' },
  approved: { kind: 'ok', label: 'Approvata' },
  rejected: { kind: 'danger', label: 'Rifiutata' },
};

export const requestState = (status) =>
  REQUEST_STATES[status] || { kind: 'muted', label: status || '—' };

// Il tipo di mezzo esiste solo sul Transfer (enum M7).
export default {
  // Senza `status` torna tutto (pending inclusi), ordinato per data di invio
  // decrescente dal server.
  getRequests(status) {
    return API.get('/booking-request', {
      params: status ? { status } : {},
    }).then((r) => (Array.isArray(r.data) ? r.data : r.data?.data ?? []));
  },

  getRequestById(id) {
    return API.get('/booking-request/' + id);
  },

  // Solo ruoli globali: dà un partner (e opzionalmente una sede) a una
  // richiesta nata senza — notturno con partenza libera, transfer.
  assignRequest(id, { partnerId, headquarterId }) {
    return API.patch(`/booking-request/${id}/assign`, {
      partnerId,
      ...(headquarterId ? { headquarterId } : {}),
    });
  },

  approveRequest(id) {
    return API.post(`/booking-request/${id}/approve`, {});
  },

  // Nuove date/orari + motivazione: la richiesta torna al cliente, che può
  // accettare (diventa `approved`) o rifiutare.
  counterProposeRequest(id, proposal) {
    return API.post(`/booking-request/${id}/counter-propose`, proposal);
  },

  rejectRequest(id, reason) {
    return API.post(`/booking-request/${id}/reject`, reason ? { reason } : {});
  },

  // ------------------------------------------------------------ PREZZO ---
  // Preventivo della richiesta con il partner e la sede ATTUALI.
  //
  // Il backend salva già `request.quote`, ma calcolato alla CREAZIONE: una
  // richiesta nata senza partner ha il listino di SIMBA MOBILITY, che non è
  // più quello giusto dopo l'assegnazione. Si richiede quindi lo stesso
  // preventivo (POST /booking-request/quote, non scrive nulla) con i dati
  // di oggi. Risposta: BookingRequestQuoteDto (`price_net` = imponibile,
  // `price_gross` = totale fattura IVA inclusa, cioè quanto paga in app).
  //
  // ⚠️ Il controller valida con `forbidNonWhitelisted`: un campo in più nel
  // body dà 400. Per questo il body si costruisce campo per campo.
  quoteForRequest(request) {
    const point = (p) => (p ? {
      address: String(p.address ?? ''),
      street_number: String(p.street_number ?? ''),
      city: String(p.city ?? ''),
      cap: String(p.cap ?? ''),
      province: String(p.province ?? ''),
      ...(Number.isFinite(Number(p.latitude)) && p.latitude != null
        ? { latitude: Number(p.latitude) } : {}),
      ...(Number.isFinite(Number(p.longitude)) && p.longitude != null
        ? { longitude: Number(p.longitude) } : {}),
    } : undefined);
    const r = request;
    const body = {
      service: r.service,
      partnerId: r.partnerId || undefined,
      headquarterId: r.headquarterId || undefined,
      carId: r.carId || undefined,
      leg_type: r.leg_type || undefined,
      pickup_point: point(r.pickup_point),
      dropoff_point: point(r.dropoff_point),
      outbound_date: r.outbound_date ? String(r.outbound_date).slice(0, 10) : undefined,
      outbound_time: r.outbound_time ? String(r.outbound_time).slice(0, 5) : undefined,
      return_date: r.return_date ? String(r.return_date).slice(0, 10) : undefined,
      return_time: r.return_time ? String(r.return_time).slice(0, 5) : undefined,
    };
    Object.keys(body).forEach((k) => body[k] === undefined && delete body[k]);
    return API.post('/booking-request/quote', body, { skipErrorToast: true })
      .then((res) => res.data);
  },

  // Fascia km usata per un servizio: /tariff-band/quote dice QUALE fascia
  // (band_id), la fascia stessa porta i confini min_km / max_km.
  async bandFor({ partnerId, service, km, date }) {
    const q = await API.post('/tariff-band/quote', {
      partnerId: partnerId || null,
      km,
      date,
      service,
    }, { skipErrorToast: true });
    const bandId = q.data?.band_id;
    if (!bandId) return null;
    const b = await API.get(`/tariff-band/${bandId}`, { skipErrorToast: true });
    return b.data || null;
  },

  // Solo da `approved`, e solo con partner + sede valorizzati. Passa per le
  // guardie di BookingService (capability del partner).
  // `driverCompensation` è { gone, return }: il compenso del driver per gamba,
  // concordato accettando (è così che si paga un notturno). Chi lo prende non
  // è ancora assegnato: l'importo resta sulla gamba e aspetta il driver.
  // `force`: converte anche senza copertura driver verificata. Senza, una
  // gamba per cui il planner non trova nessuno libero fa fallire la
  // conversione («No drivers available», «No jolly available for this hour»).
  // Con `force` la prenotazione nasce lo stesso, con gli orari richiesti e i
  // planner scoperti: esiste ed è assegnabile, ma nessuno ha verificato che
  // qualcuno possa farla.
  convertRequest(id, { customerPrice, driverCompensation, force } = {}) {
    return API.post(`/booking-request/${id}/convert`, {
      ...(customerPrice != null ? { customer_price: customerPrice } : {}),
      ...(driverCompensation ? { driver_compensation: driverCompensation } : {}),
      ...(force ? { force: true } : {}),
    });
  },
};
